import { Contractor } from "@/api/williams/contractors";
import { ContractorSchema } from "./ContractorSchema";
import { ErrorAlert } from "../components/alerts";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../routes";
import { SavePrompt } from "@/components";
import { SpinButton } from "@/components/form/SpinButton";
import { useContractorValidation, useSaveContractor } from "./hooks";
import { useForm } from "react-hook-form";
import { useLoadingScreen } from "@/hooks";
import { useNavigate } from "react-router";
import { useRegionsLookup } from "../scopeOfWork/hooks";
import { useWeldingSpecialists } from "../users/hooks";
import { zodResolver } from "@hookform/resolvers/zod";

export type ContractorFormProps = {
  data: Contractor;
};

export function ContractorForm({ data }: ContractorFormProps) {
  const save = useSaveContractor();
  const navigate = useNavigate();
  const regionsLookup = useRegionsLookup();
  const specialists = useWeldingSpecialists();
  const loader = useLoadingScreen();

  const form = useForm<Contractor>({
    defaultValues: data,
    resolver: zodResolver(ContractorSchema),
    reValidateMode: "onChange",
  });

  const formData = form.watch();
  const regions = formData.regions ?? [];
  const validate = useContractorValidation(form);
  const errors = form.formState.errors;
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  function handleSelectRegions() {
    regionsLookup.showDialog({
      selectedRegions: regions,
      onAccept: (values) => {
        form.clearErrors("regions");
        form.setValue("regions", values, {
          shouldDirty: true,
        });
      },
    });
  }

  function handleSelectWeldingSpecialist(id: string) {
    if (specialists.data) {
      const specialist = specialists.data.find((x) => x.id === id);
      form.clearErrors("weldingSpecialist");
      form.setValue("weldingSpecialist", specialist, {
        shouldDirty: true,
      });
    }
  }

  async function handleSave() {
    loader.show();
    if ((await validate(formData)) !== true) {
      loader.hide();
      return;
    }
    save.mutateAsync(formData, {
      onSuccess: (contractor) =>
        navigate(getFullPath(routes.contractor.open(contractor.id)), {
          state: {
            skipSavePrompt: true,
          },
        }),
    });
  }

  return (
    <SavePrompt when={isDirty}>
      <h3 className="mb-2">Contractor</h3>

      <form className="flex-fill" onSubmit={form.handleSubmit(handleSave)} style={{ maxWidth: 450 }}>
        {/* Contractor Name */}
        <div>
          <label htmlFor="name" className="col-form-label pb-0">
            Contractor Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.name && "border-danger"}`}
            autoComplete="off"
            {...form.register("name")}
          />
          {errors.name && <span className="text-danger">{errors.name.message}</span>}
        </div>
        {/* Address Line 1 */}
        <div>
          <label htmlFor="address1" className="col-form-label pb-0">
            Address Line 1
          </label>
          <input
            type="text"
            className={`form-control ${errors.addressLine1 && "border-danger"}`}
            autoComplete="off"
            {...form.register("addressLine1")}
          />
          {errors.addressLine1 && <span className="text-danger">{errors.addressLine1.message}</span>}
        </div>
        {/* Address Line 2 */}
        <div>
          <label htmlFor="address2" className="col-form-label pb-0">
            Address Line 2
          </label>
          <input
            type="text"
            className={`form-control ${errors.addressLine2 && "border-danger"}`}
            autoComplete="off"
            {...form.register("addressLine2")}
          />
          {errors.addressLine2 && <span className="text-danger">{errors.addressLine2.message}</span>}
        </div>
        {/* Address Line 3 */}
        <div>
          <label htmlFor="address3" className="col-form-label pb-0">
            Address Line 3
          </label>
          <input
            type="text"
            className={`form-control ${errors.addressLine3 && "border-danger"}`}
            autoComplete="off"
            {...form.register("addressLine3")}
          />
          {errors.addressLine3 && <span className="text-danger">{errors.addressLine3.message}</span>}
        </div>
        {/* Postal/Zip Code */}
        <div>
          <label htmlFor="postalCode" className="col-form-label pb-0">
            Postal/Zip Code
          </label>
          <input
            type="text"
            className={`form-control ${errors.postalCode && "border-danger"}`}
            autoComplete="off"
            {...form.register("postalCode")}
          />
          {errors.postalCode && <span className="text-danger">{errors.postalCode.message}</span>}
        </div>
        {/* Country */}
        <div>
          <label htmlFor="country" className="col-form-label pb-0">
            Country
          </label>
          <input
            type="text"
            className={`form-control ${errors.country && "border-danger"}`}
            autoComplete="off"
            {...form.register("country")}
          />
          {errors.country && <span className="text-danger">{errors.country.message}</span>}
        </div>
        {/* Phone Number */}
        <div>
          <label htmlFor="phoneNumber" className="col-form-label pb-0">
            Phone Number
          </label>
          <input
            type="text"
            className={`form-control ${errors.phoneNumber && "border-danger"}`}
            autoComplete="off"
            {...form.register("phoneNumber")}
          />
          {errors.phoneNumber && <span className="text-danger">{errors.phoneNumber.message}</span>}
        </div>
        {/* Email Address */}
        <div>
          <label htmlFor="emailAddress" className="col-form-label pb-0">
            Email Address
          </label>
          <input
            type="text"
            className={`form-control ${errors.emailAddress && "border-danger"}`}
            autoComplete="off"
            {...form.register("emailAddress")}
          />
          {errors.emailAddress && <span className="text-danger">{errors.emailAddress.message}</span>}
        </div>
        {/* Shop or Field */}
        <div>
          <label htmlFor="shopOrField" className="col-form-label pb-0">
            Shop or Field
          </label>
          <select className={`form-select ${errors.shopOrField && "border-danger"}`} {...form.register("shopOrField")}>
            <option />
            <option>Shop</option>
            <option>Field</option>
            <option>Both</option>
          </select>
          {errors.shopOrField && <span className="text-danger">{errors.shopOrField.message}</span>}
        </div>
        {/* Welding Specialistt */}
        <div>
          <label htmlFor="weldingSpecialist" className="col-form-label pb-0">
            Welding Specialist
          </label>
          <select
            className={`form-select ${errors.weldingSpecialist && "border-danger"}`}
            value={form.getValues("weldingSpecialist.id") ?? undefined}
            onChange={(e) => handleSelectWeldingSpecialist(e.target.value)}
          >
            <option />
            {specialists.data &&
              specialists.data.map((specialist) => (
                <option key={specialist.id} value={specialist.id}>
                  {specialist.name ?? specialist.userName}
                </option>
              ))}
          </select>
          {errors.weldingSpecialist && <span className="text-danger">{errors.weldingSpecialist.message}</span>}
        </div>
        {/* Regions */}
        <div>
          <label htmlFor="regions" className="col-form-label pb-0">
            Region(s)
          </label>
          <div className="input-group">
            <input
              type="text"
              className={`form-control ${errors.regions && "border-danger"}`}
              readOnly
              value={regions.map((region) => region.name).join(", ")}
            />
            <span className="input-group-text" role="button" onClick={handleSelectRegions}>
              <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
            </span>
          </div>
          {errors.regions && <span className="text-danger">{errors.regions.message}</span>}
        </div>

        <SpinButton type="submit" className="btn btn-default mt-2 mb-3" disabled={loader.visible || isDirty !== true}>
          Save
        </SpinButton>

        {errors.root && <ErrorAlert>{errors.root?.message}</ErrorAlert>}
      </form>
    </SavePrompt>
  );
}
